import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import type { JSX } from "react";

import { toast } from "../../../components/ui/use-toast.js";
import { ApiError } from "../../../lib/api.js";
import { Markdown } from "../../documentation/markdown.js";
import { marketingApi } from "../client.js";
import { PlatformPill, draftDisplayTitle, humanAge } from "../shared.js";
import type { Draft } from "../types.js";

/** Friendly "Jun 30, 09:00" for the approve toast — full enough for a slot
 *  that may be days out (the calendar's `localTime` is time-only). */
function scheduledLabel(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const errorDescription = (e: unknown): string =>
  e instanceof ApiError ? e.message : "Please retry.";

/**
 * Modal that opens when the operator clicks a row in the
 * `PendingApprovalTable`. Replaces the inline `DraftCard` editor
 * that used to render every draft in full inside the page; the table
 * now keeps the list scannable and this dialog owns the heavy
 * "approve / deny / edit / regenerate" interactions.
 *
 * Behaviour:
 *  - View mode: image (if any) + title + body + sources, four
 *    actions in the footer (Approve & schedule / Deny / Edit /
 *    Re-generate).
 *  - Edit mode: title + body become inputs, footer flips to
 *    Save edit / Cancel.
 *  - Every successful action invalidates the queue + schedule query,
 *    closes the dialog, and the parent table re-fetches.
 */
export function DraftReviewDialog({
  draft,
  open,
  onClose,
}: {
  draft: Draft | null;
  open: boolean;
  onClose: () => void;
}): JSX.Element | null {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(draft?.body ?? "");
  const [title, setTitle] = useState(draft?.title ?? "");

  // Re-sync local edit buffers when a different draft is opened, or
  // the same draft gets re-fetched.
  useEffect(() => {
    setBody(draft?.body ?? "");
    setTitle(draft?.title ?? "");
    setEditing(false);
  }, [draft?.id, draft?.body, draft?.title]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["marketing", "queue"] });
    qc.invalidateQueries({ queryKey: ["marketing", "schedule"] });
    // The History tab's Approved / Published / Rejected lists key off
    // ["marketing","drafts",…] — refresh them too so acting on an already-
    // approved draft from there (Deny / Edit / Re-generate) updates the list.
    qc.invalidateQueries({ queryKey: ["marketing", "drafts"] });
  };

  // Approve flips the draft to `approved` AND schedules it synchronously on the
  // backend (operator-driven, so not pause-gated), returning the draft with its
  // `suggested_post_at` slot. Surfacing that here is the fix for "I approved and
  // the post vanished — where did it go?": the draft leaves this queue, so the
  // toast tells the operator it's now on the Schedule tab (and when), instead of
  // closing silently.
  const approve = useMutation({
    mutationFn: () =>
      marketingApi.reviewDraft(draft!.id, {
        action: "approve",
        auto_publishable: true,
      }),
    onSuccess: (updated: Draft) => {
      invalidate();
      onClose();
      toast(
        updated.suggested_post_at
          ? {
              title: "Approved & scheduled",
              description: `Posting ${scheduledLabel(updated.suggested_post_at)} — see the Schedule tab. Nothing publishes until automated publishing is on.`,
            }
          : {
              title: "Approved",
              description:
                "It'll appear on the Schedule tab once a posting slot is assigned.",
            },
      );
    },
    onError: (e) =>
      toast({
        variant: "destructive",
        title: "Couldn't approve the draft",
        description: errorDescription(e),
      }),
  });
  const reject = useMutation({
    mutationFn: () =>
      marketingApi.reviewDraft(draft!.id, { action: "reject" }),
    onSuccess: () => {
      invalidate();
      onClose();
      toast({ title: "Draft denied", description: "Removed from the review queue." });
    },
    onError: (e) =>
      toast({
        variant: "destructive",
        title: "Couldn't deny the draft",
        description: errorDescription(e),
      }),
  });
  const saveEdit = useMutation({
    mutationFn: () =>
      marketingApi.reviewDraft(draft!.id, {
        action: "edit",
        body,
        title: title || null,
      }),
    onSuccess: () => {
      invalidate();
      setEditing(false);
      toast({ title: "Edit saved" });
    },
    onError: (e) =>
      toast({
        variant: "destructive",
        title: "Couldn't save the edit",
        description: errorDescription(e),
      }),
  });
  const regen = useMutation({
    mutationFn: () => marketingApi.regenerateDraft(draft!.id, {}),
    onSuccess: () => {
      invalidate();
      onClose();
      toast({ title: "Re-generating", description: "A fresh draft will appear in the queue." });
    },
    onError: (e) =>
      toast({
        variant: "destructive",
        title: "Couldn't re-generate the draft",
        description: errorDescription(e),
      }),
  });

  // The review QUEUE list no longer ships `image_b64` (the composited card
  // PNGs are ~0.6 MB each — far too heavy to return for the whole pending
  // backlog). Fetch the full draft by id when the dialog opens so the image
  // still shows; falls back to whatever the list row carried.
  const full = useQuery({
    queryKey: ["marketing", "draft", draft?.id],
    queryFn: () => marketingApi.getDraft(draft!.id),
    enabled: open && draft != null && !draft.image_b64,
  });

  if (!draft) return null;

  const imageB64 = draft.image_b64 ?? full.data?.image_b64 ?? null;
  const thumb = imageB64 ? `data:image/png;base64,${imageB64}` : null;
  const busy =
    approve.isPending ||
    reject.isPending ||
    saveEdit.isPending ||
    regen.isPending;

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose()}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { maxWidth: "42rem" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pr: 6 }}>
        <PlatformPill platform={draft.platform} />
        <Box
          component="span"
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {draftDisplayTitle(draft)}
        </Box>
        <IconButton
          aria-label="Close"
          onClick={() => !busy && onClose()}
          disabled={busy}
          size="small"
          sx={{ position: "absolute", top: 12, right: 12, color: "text.secondary" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2 }}>
        <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
          {draft.generated_by === "human" ? "Created by you" : "Generated by the agent"}
          {" · "}
          {humanAge(draft.created_at)}
        </Typography>

        {thumb ? (
          <Box
            component="img"
            src={thumb}
            alt={draft.image_prompt ?? "draft visual"}
            sx={{ aspectRatio: "16 / 9", width: "100%", borderRadius: 1, objectFit: "cover" }}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              aspectRatio: "16 / 9",
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 1,
              bgcolor: (t) => alpha(t.palette.text.secondary, 0.08),
              fontSize: 12,
              color: "text.disabled",
            }}
          >
            {draft.image_prompt ? "image generation skipped" : "text-only platform"}
          </Box>
        )}

        {draft.image_model &&
          (draft.image_model.includes("brand-gradient") ? (
            <Typography
              data-testid="image-source-fallback"
              sx={{ fontSize: 12, color: "warning.main" }}
            >
              Fallback image (branded gradient) — the real AI photo couldn't be
              generated
              {typeof draft.metadata?.image_error === "string"
                ? `: ${draft.metadata.image_error}`
                : ""}
              . Check the AI gateway's OpenAI image access (OPENAI_API_KEY +
              gpt-image-1 org verification).
            </Typography>
          ) : (
            <Typography
              data-testid="image-source-ai"
              sx={{ fontSize: 12, color: "text.secondary" }}
            >
              AI photo · {draft.image_model.replace("composited:", "")}
            </Typography>
          ))}

        {editing ? (
          <Box sx={{ display: "grid", gap: 1.5 }}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              disabled={busy}
              placeholder="(no title)"
              fullWidth
            />
            <TextField
              label="Body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={busy}
              multiline
              rows={Math.max(8, Math.min(20, body.split("\n").length + 2))}
              fullWidth
              inputProps={{ sx: { fontFamily: "ui-monospace, monospace", fontSize: 12 } }}
            />
          </Box>
        ) : (
          <Box sx={{ display: "grid", gap: 1.5 }}>
            {draft.title && (
              <Typography component="h3" sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.25 }}>
                {draft.title}
              </Typography>
            )}
            {/* Render the body as Markdown so **bold** / lists / links show
                formatted instead of leaking raw `**` markers — the agent
                writes the post in Markdown. Edit mode keeps the raw source. */}
            <Box sx={{ fontSize: 14, "& p:first-of-type": { mt: 0 } }}>
              <Markdown source={draft.body} />
            </Box>
            {draft.generation_sources?.length > 0 && (
              <Box component="details" sx={{ fontSize: 12, color: "text.disabled" }}>
                <Box component="summary" sx={{ cursor: "pointer" }}>
                  Why this? ({draft.generation_sources.length} source
                  {draft.generation_sources.length === 1 ? "" : "s"})
                </Box>
                <Box component="ul" sx={{ mt: 0.5, display: "grid", gap: 0.5, m: 0, pl: 2 }}>
                  {draft.generation_sources.slice(0, 8).map((s) => (
                    <Box component="li" key={s.index}>
                      <Box component="span" sx={{ fontFamily: "ui-monospace, monospace" }}>
                        [{s.index}]
                      </Box>{" "}
                      <Box component="span" sx={{ fontWeight: 500 }}>
                        {s.title || s.source_id}
                      </Box>
                      {" · "}
                      {s.source_app}/{s.source_type}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {editing ? (
          <>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              size="small"
              onClick={() => {
                setEditing(false);
                setBody(draft.body);
                setTitle(draft.title ?? "");
              }}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="contained"
              size="small"
              onClick={() => saveEdit.mutate()}
              disabled={busy}
            >
              {saveEdit.isPending ? "Saving…" : "Save edit"}
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              size="small"
              onClick={() => regen.mutate()}
              disabled={busy}
              title="Re-run the AI with the same trend"
            >
              {regen.isPending ? "…" : "Re-generate"}
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              size="small"
              onClick={() => setEditing(true)}
              disabled={busy}
            >
              Edit
            </Button>
            <Button
              type="button"
              variant="outlined"
              color="inherit"
              size="small"
              onClick={() => reject.mutate()}
              disabled={busy}
            >
              {reject.isPending ? "Denying…" : "Deny"}
            </Button>
            <Button
              type="button"
              variant="contained"
              size="small"
              onClick={() => approve.mutate()}
              disabled={busy}
              title="Approve and let the publisher ship it on schedule"
            >
              {approve.isPending ? "Approving…" : "Approve & schedule"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
